import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { FileDropzone } from "@/components/FileDropzone";
import { money } from "@/lib/api";
import { Banknote, ArrowRightLeft } from "lucide-react";

export default function LiquidationDialog({ open, onOpenChange, onSave, colado, colador, suggested }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("efectivo");
  const [notes, setNotes] = useState("");
  const [evidence, setEvidence] = useState({ path: null, name: null });

  useEffect(() => {
    if (open) {
      setAmount(suggested != null ? String(suggested) : "");
      setMethod("efectivo");
      setNotes("");
      setEvidence({ path: null, name: null });
    }
  }, [open, suggested]);

  const title = colado ? "Liquidar colado" : `Registrar pago / adelanto`;

  const save = () => {
    onSave({
      amount: Number(amount) || 0,
      method,
      notes,
      colado_id: colado ? colado.id : null,
      evidence_file_path: evidence.path,
      evidence_file_name: evidence.name,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="liquidation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {colador && <p className="text-sm text-slate-500 -mt-2">Colador: <b className="text-slate-800">{colador.name}</b></p>}
          {colado && (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <span className="text-slate-500">{colado.obra || "Colado"} · </span>
              <span className="font-medium text-slate-800">{money(suggested)}</span>
            </div>
          )}

          <div>
            <Label>Método de pago</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              <button type="button" onClick={() => setMethod("efectivo")}
                className={`flex items-center justify-center gap-2 rounded-md border py-2.5 text-sm font-medium transition-colors ${method === "efectivo" ? "border-orange-600 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                data-testid="method-efectivo-btn">
                <Banknote className="h-4 w-4" /> Efectivo
              </button>
              <button type="button" onClick={() => setMethod("transferencia")}
                className={`flex items-center justify-center gap-2 rounded-md border py-2.5 text-sm font-medium transition-colors ${method === "transferencia" ? "border-orange-600 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                data-testid="method-transferencia-btn">
                <ArrowRightLeft className="h-4 w-4" /> Transferencia
              </button>
            </div>
          </div>

          <div>
            <Label>Cantidad entregada</Label>
            <Input type="number" step="any" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00" className="mt-1.5" data-testid="liquidation-amount-input" />
          </div>

          {method === "transferencia" && (
            <div>
              <Label className="mb-1.5 block">Comprobante de transferencia</Label>
              <FileDropzone accept="image/*" kind="imagen" value={evidence.path} filename={evidence.name}
                onUploaded={(path, name) => setEvidence({ path, name })}
                onClear={() => setEvidence({ path: null, name: null })} testid="evidence-drop" />
            </div>
          )}

          <div>
            <Label>Nota / referencia</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Opcional" className="mt-1.5" data-testid="liquidation-notes-input" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="liquidation-cancel-btn">Cancelar</Button>
          <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="liquidation-save-btn">Registrar pago</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
